import { Select, SelectItem, Tab, Tabs } from '@heroui/react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ActionBlock, ContentLoadError, Spinner } from '../../../../components';
import {
  AD_ANALYTICS_RANGES,
  AD_PLATFORM_LABELS,
  AdAnalytics,
  AdPlatformCounts,
  useGetAdAnalytics,
} from '../../../../queries';
import { useHotelOptions } from '../../hooks';

interface AdStatsProps {
  adId: string;
  /** Hotele przypisane do reklamy — tylko w nich mogła się wyświetlić. */
  assignedHotels: string[];
}

/** Wartość „bez zawężenia” — `Select` w HeroUI nie przyjmuje pustego klucza. */
const ALL_HOTELS = 'all';

const IMPRESSION_COLOR = '#0a7465';
const CLICK_COLOR = '#f59e0b';

const numberFormat = new Intl.NumberFormat('pl-PL');

/** `DD-MM-YYYY` z backendu → `DD.MM` na osi; pełna data zostaje w tooltipie. */
const shortDate = (date: string): string => date.split('-').slice(0, 2).join('.');

const platformRows = (counts: AdPlatformCounts): Array<{ name: string; value: number }> => (
  Object.entries(AD_PLATFORM_LABELS) as Array<[keyof AdPlatformCounts, string]>
)
  .map(([key, name]) => ({ name, value: counts[key] }))
  .filter(({ value }) => value > 0);

const StatTile: FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="flex-1 min-w-[9rem] rounded-md border border-default-200 px-4 py-3">
    <p className="text-tiny uppercase tracking-wide text-default-500">{label}</p>
    <p className="text-2xl font-semibold leading-tight">{value}</p>
    {hint && <p className="text-tiny text-default-400">{hint}</p>}
  </div>
);

const ChartCard: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex-1 min-w-[18rem]">
    <p className="mb-2 text-small font-medium text-default-600">{title}</p>
    <div className="h-56">{children}</div>
  </div>
);

const Charts: FC<{ data: AdAnalytics }> = ({ data }) => {
  const platforms = useMemo(
    () => platformRows(data.byPlatform.impressions),
    [data.byPlatform.impressions],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        <StatTile label="Wyświetlenia" value={numberFormat.format(data.totals.impressions)} />
        <StatTile label="Kliknięcia" value={numberFormat.format(data.totals.clicks)} />
        <StatTile
          label="CTR"
          value={`${(data.totals.ctr * 100).toFixed(2)}%`}
          hint="klikalność"
        />
      </div>

      <ChartCard title="Przebieg dzienny">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.daily} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
            <defs>
              <linearGradient id="ad-impressions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={IMPRESSION_COLOR} stopOpacity={0.35} />
                <stop offset="100%" stopColor={IMPRESSION_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} tickMargin={6} />
            <YAxis allowDecimals={false} fontSize={11} width={44} />
            <Tooltip
              formatter={(value, name) => [
                numberFormat.format(Number(value ?? 0)),
                name === 'impressions' ? 'Wyświetlenia' : 'Kliknięcia',
              ]}
            />
            <Area
              type="monotone"
              dataKey="impressions"
              stroke={IMPRESSION_COLOR}
              fill="url(#ad-impressions)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="clicks"
              stroke={CLICK_COLOR}
              fill="none"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="flex flex-wrap gap-6">
        <ChartCard title="Wyświetlenia wg aplikacji">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={platforms}
              layout="vertical"
              margin={{ top: 4, right: 12, bottom: 0, left: 12 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.3} />
              <XAxis type="number" allowDecimals={false} fontSize={11} />
              <YAxis type="category" dataKey="name" width={92} fontSize={11} />
              <Tooltip formatter={(value) => numberFormat.format(Number(value ?? 0))} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {platforms.map((row) => (
                  <Cell key={row.name} fill={IMPRESSION_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Wyświetlenia wg godziny doby">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byHour} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis dataKey="hour" fontSize={11} interval={2} tickMargin={6} />
              <YAxis allowDecimals={false} fontSize={11} width={44} />
              <Tooltip
                formatter={(value) => [numberFormat.format(Number(value ?? 0)), 'Wyświetlenia']}
                labelFormatter={(hour) => `Godzina ${hour}:00`}
              />
              <Bar dataKey="impressions" fill={IMPRESSION_COLOR} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

/**
 * Statystyki reklamy na jej szczegółach. Zliczanie robią zdarzenia
 * `AD_IMPRESSION` i `AD_CLICK` wysyłane przez aplikacje gościa, a liczby
 * czytamy z GA4 — stąd zera przez pierwszą dobę po dodaniu reklamy.
 *
 * Zawężenie do hotelu działa też na danych zebranych wcześniej: klienci od
 * początku dokładają `organization_id` do każdego zdarzenia, więc backend ma po
 * czym filtrować bez wydawania nowych wersji aplikacji.
 */
export const AdStats: FC<AdStatsProps> = ({ adId, assignedHotels }) => {
  const [days, setDays] = useState<number>(AD_ANALYTICS_RANGES[1]);
  const [hotelKey, setHotelKey] = useState<string>(ALL_HOTELS);
  const { hotelNameById } = useHotelOptions();

  const hotelOptions = useMemo(() => assignedHotels
    .map((id) => ({ id, name: hotelNameById.get(id) || id }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pl')), [assignedHotels, hotelNameById]);

  // Odpięcie hotelu nie może zostawić filtra na obiekcie spoza listy — raport
  // pokazywałby wtedy pustkę, a przełącznik nie miałby czego podświetlić.
  useEffect(() => {
    if (hotelKey !== ALL_HOTELS && !assignedHotels.includes(hotelKey)) setHotelKey(ALL_HOTELS);
  }, [assignedHotels, hotelKey]);

  const hotelId = hotelKey === ALL_HOTELS ? undefined : hotelKey;
  const { data, isLoading, error } = useGetAdAnalytics(adId, days, hotelId);

  const hasTraffic = !!data && (data.totals.impressions > 0 || data.totals.clicks > 0);
  const hotelName = hotelId ? hotelNameById.get(hotelId) || hotelId : null;

  return (
    <ActionBlock
      title="Statystyki wyświetleń"
      description="Wyświetlenia i kliknięcia zbierane z aplikacji gościa. Google Analytics przelicza raporty raz na dobę, więc ostatnie godziny mogą być jeszcze niepełne."
      action={(
        <div className="flex flex-wrap items-center gap-2">
          {/* Przy jednym hotelu suma i tak dotyczy tylko jego — przełącznik
              byłby wtedy wyborem bez alternatywy. */}
          {hotelOptions.length > 1 && (
            <Select
              size="sm"
              radius="sm"
              className="min-w-[13rem]"
              aria-label="Hotel"
              selectedKeys={new Set([hotelKey])}
              onSelectionChange={(keys) => {
                const [next] = Array.from(keys as Set<string>);
                if (next) setHotelKey(next);
              }}
            >
              <>
                <SelectItem key={ALL_HOTELS} textValue="Wszystkie hotele">
                  Wszystkie hotele
                </SelectItem>
                {hotelOptions.map((hotel) => (
                  <SelectItem key={hotel.id} textValue={hotel.name}>
                    {hotel.name}
                  </SelectItem>
                ))}
              </>
            </Select>
          )}

          <Tabs
            size="sm"
            radius="sm"
            selectedKey={String(days)}
            onSelectionChange={(key) => setDays(Number(key))}
            aria-label="Zakres statystyk"
          >
            {AD_ANALYTICS_RANGES.map((range) => (
              <Tab key={String(range)} title={`${range} dni`} />
            ))}
          </Tabs>
        </div>
      )}
    >
      {isLoading && (
        <div className="py-10 flex justify-center">
          <Spinner showLabel={false} />
        </div>
      )}

      {!isLoading && error && <ContentLoadError message={error.message} />}

      {!isLoading && !error && !hasTraffic && (
        <p className="py-6 text-small text-default-500">
          {hotelName
            ? `Brak zarejestrowanych wyświetleń w hotelu ${hotelName} w wybranym okresie.`
            : 'Brak zarejestrowanych wyświetleń w wybranym okresie.'}
          {' '}
          Jeśli reklama została przypisana do hoteli w ciągu ostatniej doby, dane
          pojawią się po przeliczeniu raportów przez Google Analytics.
        </p>
      )}

      {!isLoading && !error && hasTraffic && data && <Charts data={data} />}
    </ActionBlock>
  );
};
